import React, { useState, useEffect, useContext } from 'react';
import AuthContext from '../../../context/AuthContext';
import api from '../../../utils/api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './SlotRequestList.css';

const SlotRequestList = () => {
  const { user } = useContext(AuthContext);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');

  // Fetch slot requests
  useEffect(() => {
    const fetchRequests = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/slot-requests/interviewer?status=${statusFilter}`);
        setRequests(response.data);
      } catch (err) {
        console.error('Error fetching slot requests:', err);
        toast.error('Failed to load slot requests');
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, [statusFilter]);

  // Handle status update (fulfill/reject)
  const handleStatusUpdate = async (requestId, status) => {
    const actionText = status === 'approved' ? 'fulfill' : 'reject';
    if (window.confirm(`Are you sure you want to ${actionText} this request?`)) {
      try {
        const response = await api.put(`/api/slot-requests/${requestId}/status`, { status });
        
        // Update local state with the response data (which will have 'fulfilled' status if approved)
        setRequests(requests.map(req => 
          req._id === requestId ? response.data : req
        ));
        
        const successMessage = status === 'approved' ? 
          'Request fulfilled and slot created successfully' : 
          'Request rejected successfully';
        toast.success(successMessage);
      } catch (err) {
        console.error('Error updating request status:', err);
        toast.error(err.response?.data?.message || 'Failed to update request status');
      }
    }
  };

  // No fulfillment functions needed as approval automatically creates slots

  // Format date for display
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return 'Invalid date';
    }
  };

  // Get badge class based on status
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending':
        return 'badge-warning';
      case 'rejected':
        return 'badge-danger';
      case 'fulfilled':
        return 'badge-success';
      default:
        return 'badge-secondary';
    }
  };

  return (
    <div className="slot-request-list">
      <h1>Slot Requests</h1>
      
      <div className="filter-container">
        <div className="status-filter">
          <label>Filter by status:</label>
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
            <option value="fulfilled">Fulfilled</option>
          </select>
        </div>
      </div>
      
      {loading ? (
        <div className="loading">Loading requests...</div>
      ) : requests.length === 0 ? (
        <div className="no-requests">
          <p>No {statusFilter || 'slot'} requests found.</p>
        </div>
      ) : (
        <div className="request-items">
          {requests.map((request) => (
            <div key={request._id} className="request-item">
              <div className="request-header">
                <div className="request-candidate">
                  <h3>{request.candidate?.name || 'Unknown User'}</h3>
                  <span className={`badge ${getStatusBadgeClass(request.status)}`}>
                    {request.status}
                  </span>
                </div>
                <div className="request-meta">
                  <span>{request.interviewType} Interview</span>
                  <span>•</span>
                  <span>Requested on: {formatDate(request.createdAt)}</span>
                </div>
              </div>
              
              <div className="request-details">
                <div className="detail-group">
                  <label>Preferred Date:</label>
                  <span>{formatDate(request.preferredDate)}</span>
                </div>
                <div className="detail-group">
                  <label>Preferred Time:</label>
                  <span>{request.preferredTime} ({request.timezone})</span>
                </div>
                {request.message && (
                  <div className="detail-group">
                    <label>Message:</label>
                    <p className="request-message">{request.message}</p>
                  </div>
                )}
              </div>

              <div className="request-actions">
                {request.status === 'pending' && (
                  <>
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => handleStatusUpdate(request._id, 'approved')}
                    >
                      Fulfill & Create Slot
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleStatusUpdate(request._id, 'rejected')}
                    >
                      Reject
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SlotRequestList;
